/**
 * Deposit Bonuses API Service
 *
 * Fetches the eligible deposit bonus plans for a payment method + amount from
 * /api/psp/apcopay/deposit/bonuses/ (via the bb_get_deposit_bonus_plans AJAX
 * action). The response carries the server-computed bonus amount plus the
 * min/max deposit guards used to enforce eligibility in the deposit form.
 *
 * @package betbeast
 */

export class DepositBonusesAPI {
    constructor() {
        this.ajaxUrl = window.accountData?.ajaxUrl || '/wp-admin/admin-ajax.php';
        this.nonce = window.accountData?.nonce || '';

        if (!this.nonce) {
            console.error('❌ Deposit bonuses: AJAX nonce not found. Check wp_localize_script setup.');
        }
    }

    /**
     * Fetch eligible bonus plans for the given method + amount.
     *
     * @param {string} paymentMethod API method code (e.g. APCOPAY_V2_CARD)
     * @param {number} amount        Deposit amount
     * @returns {Promise<Array>} Normalized plans, or [] on any failure.
     *   Each plan: { bonus_plan_id, name, bonus_amount, min_deposit_amount,
     *   max_deposit_amount, playable, currency }
     */
    async fetchEligibleBonusPlans(paymentMethod, amount) {
        const amt = parseFloat(amount) || 0;
        if (!paymentMethod || amt <= 0) {
            return [];
        }

        const formData = new FormData();
        formData.append('action', 'bb_get_deposit_bonus_plans');
        formData.append('nonce', this.nonce);
        formData.append('payment_method', paymentMethod);
        formData.append('amount', amt);

        try {
            const response = await fetch(this.ajaxUrl, {
                method: 'POST',
                credentials: 'same-origin',
                body: formData
            });

            const data = await response.json();

            if (!data.success || !data.data || !Array.isArray(data.data.bonusPlans)) {
                return [];
            }

            return data.data.bonusPlans;
        } catch (error) {
            console.error('❌ Deposit bonus plans fetch error:', error);
            return [];
        }
    }
}
